import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Target, AlertCircle, ChevronRight, 
  Sliders, TrendingUp, Brain, Heart, MessageSquare, 
  Monitor, CheckCircle, AlertTriangle, Sparkles
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDisciplineStore } from '../../contexts/disciplineStore';
import { useGoalSuggestion } from './hooks/useGoalSuggestion';
import { 
  formatDuration, 
  calculateTotalCommitment, 
  calculateAbortRisk, 
  getBalanceAssessment,
  DIFFICULTY_MULTIPLIERS,
  REPEAT_PATTERNS
} from './lib/durationMath';

const CreateDisciplineGoalModal = ({ 
  onClose, 
  onAddGoal, 
  onSubmit,
  isDarkMode = false, 
  categories = []
}) => {
  const { t } = useTranslation('digitalDiscipline');
  // Core state
  const [goalTitle, setGoalTitle] = useState('');
  const [goalDescription, setGoalDescription] = useState('');
  const [goalDomain, setGoalDomain] = useState('digital');
  const [duration, setDuration] = useState(25); // minutes
  const [difficulty, setDifficulty] = useState('medium');
  const [repeatPattern, setRepeatPattern] = useState('once');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Progressive disclosure
  const [currentStep, setCurrentStep] = useState('quick'); // quick, advanced, review

  // Store integration
  const { addGoal, goals = [], timers = [] } = useDisciplineStore();

  // AI suggestion integration
  const goalSuggestion = useGoalSuggestion({
    category: goalDomain,
    duration,
    difficulty,
    enabled: currentStep === 'quick'
  });

  // Domain configurations with iOS-26 colors
  const domains = [
    { 
      id: 'mental', 
      label: t('goals.domain.mental', 'Zihinsel'), 
      icon: Brain, 
      color: '#007AFF',
      gradient: 'from-blue-500 to-blue-600',
      description: t('goals.domain.mental.desc', 'Odak, meditasyon, öğrenme')
    },
    { 
      id: 'physical', 
      label: t('goals.domain.physical', 'Fiziksel'), 
      icon: Heart, 
      color: '#FF3B30',
      gradient: 'from-red-500 to-red-600',
      description: t('goals.domain.physical.desc', 'Egzersiz, hareket, sağlık')
    },
    { 
      id: 'social', 
      label: t('goals.domain.social', 'Sosyal'), 
      icon: MessageSquare, 
      color: '#34C759',
      gradient: 'from-green-500 to-green-600',
      description: t('goals.domain.social.desc', 'İletişim, ilişkiler, networking')
    },
    { 
      id: 'digital', 
      label: t('goals.domain.digital', 'Dijital'), 
      icon: Monitor, 
      color: '#5856D6',
      gradient: 'from-purple-500 to-purple-600',
      description: t('goals.domain.digital.desc', 'Üretkenlik, dijital detoks, odak')
    }
  ];

  // Computed values
  const selectedDomain = domains.find(d => d.id === goalDomain);
  const totalCommitment = calculateTotalCommitment(duration, difficulty, repeatPattern);
  // Fallback abort rate from current timers (cancelled vs completed)
  const cancelledCount = Array.isArray(timers) ? timers.filter(t => t?.isCancelled).length : 0;
  const completedCount = Array.isArray(timers) ? timers.filter(t => t?.isCompleted).length : 0;
  const abortRate = (cancelledCount + completedCount) > 0 
    ? (cancelledCount / (cancelledCount + completedCount)) 
    : 0.1;
  const abortRisk = calculateAbortRisk(totalCommitment, { weekMinutes: 0, abortRate });
  const balanceAssessment = getBalanceAssessment(
    { domain: goalDomain, duration: totalCommitment }, 
    goals
  );

  // Quick suggestion chips
  const quickSuggestions = [
    { title: t('quick.focus', 'Odak Bloğu'), duration: 25, domain: 'digital' },
    { title: t('quick.break', 'Dinlenme'), duration: 15, domain: 'mental' },
    { title: t('quick.exercise', 'Hareket'), duration: 30, domain: 'physical' },
    { title: t('quick.connect', 'Sosyal Bağlantı'), duration: 20, domain: 'social' }
  ];

  // Auto-request AI suggestion when domain changes
  useEffect(() => {
    if (currentStep === 'quick' && goalDomain && goalSuggestion.request) {
      goalSuggestion.request();
    }
  }, [goalDomain, currentStep, goalSuggestion]);

  // Handle quick creation
  const handleQuickCreate = useCallback((suggestion) => {
    setGoalTitle(suggestion.title);
    setGoalDomain(suggestion.domain);
    setDuration(suggestion.duration);
    setCurrentStep('review');
  }, []);

  // Handle AI suggestion apply
  const handleApplyAISuggestion = useCallback(() => {
    goalSuggestion.apply({
      onApply: (suggestion) => {
        setGoalTitle(suggestion.title);
        setGoalDomain(suggestion.domain);
        setDuration(suggestion.duration);
        setGoalDescription(suggestion.reason || '');
        setCurrentStep('review');
      }
    });
  }, [goalSuggestion]);

  // Validation
  const isValid = useMemo(() => {
    return goalTitle.trim().length > 0 && duration > 0 && goalDomain;
  }, [goalTitle, duration, goalDomain]);

  // Submit handler
  const handleSubmit = useCallback(async () => {
    if (!isValid) return;
    
    setIsSubmitting(true);
    setValidationError('');
    
    try {
      const newGoal = {
        id: Date.now().toString(),
        title: goalTitle.trim(),
        description: goalDescription.trim(),
        domain: goalDomain,
        duration, // minutes
        difficulty,
        repeatPattern,
        createdAt: new Date().toISOString(),
        status: 'active',
        progress: 0,
        xpReward: Math.round(Math.sqrt(duration) * DIFFICULTY_MULTIPLIERS[difficulty] * 10)
      };

      // Use store's addGoal or fallback to provided submit handlers
      const submitFn = onAddGoal || onSubmit;
      if (typeof addGoal === 'function') {
        // Store addGoal doesn't return a result; assume success
        addGoal(newGoal);
        onClose();
      } else if (typeof submitFn === 'function') {
        await submitFn(newGoal);
        onClose();
      } else {
        setValidationError(t('goals.error.creation', 'Hedef oluşturulamadı'));
      }
    } catch (error) {
      console.error('Goal creation failed:', error);
      setValidationError(t('goals.error.unexpected', 'Beklenmeyen hata'));
    } finally {
      setIsSubmitting(false);
    }
  }, [isValid, goalTitle, goalDescription, goalDomain, duration, difficulty, repeatPattern, addGoal, onAddGoal, onSubmit, onClose, t]);

  // iOS-26 blur backdrop
  const backdropStyle = {
    background: isDarkMode 
      ? 'rgba(0, 0, 0, 0.7)' 
      : 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)'
  };

  // Dynamic vibrancy
  const cardStyle = {
    background: isDarkMode 
      ? 'rgba(28, 28, 30, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(40px) saturate(200%)',
    WebkitBackdropFilter: 'blur(40px) saturate(200%)',
    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={backdropStyle}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl"
          style={cardStyle}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with dynamic color */}
          <div className={`px-6 py-4 bg-gradient-to-r ${selectedDomain?.gradient || 'from-gray-500 to-gray-600'}`}>
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center gap-3">
                <Target size={24} className="drop-shadow-sm" />
                <div>
                  <h2 className="text-xl font-semibold">
                    {t('modals.createGoal.title', 'Yeni Hedef')}
                  </h2>
                  {currentStep === 'quick' && (
                    <p className="text-sm opacity-90">
                      {t('modals.createGoal.subtitle', 'Hızlı başlangıç')}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Quick Path */}
            {currentStep === 'quick' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* AI Suggestion Card */}
                {goalSuggestion.suggestion && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={`p-4 rounded-2xl border ${
                      isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-blue-50/80 border-blue-200'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Sparkles size={18} className="text-blue-500" />
                      <span className="font-medium text-blue-600 dark:text-blue-400">
                        {t('ai.goalSuggest.title', 'AI Önerisi')}
                      </span>
                    </div>
                    <p className="text-lg font-semibold mb-1">{goalSuggestion.suggestion.title}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {formatDuration(goalSuggestion.suggestion.durationMin, t)} • {goalSuggestion.suggestion.rationale}
                    </p>
                    <button
                      onClick={handleApplyAISuggestion}
                      className="w-full py-2 px-4 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
                    >
                      {t('modals.createGoal.applyAI', 'Bu Öneriyi Kullan')}
                    </button>
                  </motion.div>
                )}

                {/* Quick Suggestions */}
                <div>
                  <h3 className="font-medium mb-3 text-gray-800 dark:text-gray-200">
                    {t('goals.quick.suggestions', 'Hızlı Başlangıç')}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {quickSuggestions.map((suggestion, index) => {
                      const domain = domains.find(d => d.id === suggestion.domain);
                      return (
                        <button
                          key={index}
                          onClick={() => handleQuickCreate(suggestion)}
                          className={`p-3 rounded-xl border text-left transition-all hover:scale-105 ${
                            isDarkMode 
                              ? 'bg-slate-800/50 border-slate-700 hover:bg-slate-700/50' 
                              : 'bg-white/80 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <domain.icon size={16} style={{ color: domain.color }} />
                            <span className="font-medium text-sm">{suggestion.title}</span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {formatDuration(suggestion.duration, t)}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Advanced Options Toggle */}
                <button
                  onClick={() => setCurrentStep('advanced')}
                  className={`w-full p-3 rounded-xl border flex items-center justify-between transition-colors ${
                    isDarkMode 
                      ? 'bg-slate-800/30 border-slate-700 hover:bg-slate-700/30' 
                      : 'bg-gray-50/80 border-gray-200 hover:bg-gray-100/80'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Sliders size={18} />
                    <span className="font-medium">{t('goals.advanced.toggle', 'Gelişmiş Seçenekler')}</span>
                  </div>
                  <ChevronRight size={18} />
                </button>
              </motion.div>
            )}

            {/* Advanced Path */}
            {currentStep === 'advanced' && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                {/* Goal Title */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    {t('goals.field.title', 'Hedef Başlığı')}
                  </label>
                  <input
                    type="text"
                    value={goalTitle}
                    onChange={(e) => setGoalTitle(e.target.value)}
                    placeholder={t('goals.field.title.placeholder', 'Örn: Günlük odak seansı')}
                    className={`w-full px-4 py-3 rounded-xl border transition-colors ${
                      isDarkMode 
                        ? 'bg-slate-800/50 border-slate-700 text-white placeholder-gray-400' 
                        : 'bg-white/80 border-gray-200 text-gray-900 placeholder-gray-500'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                </div>

                {/* Domain Selection */}
                <div>
                  <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">
                    {t('goals.field.domain', 'Alan')}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {domains.map((domain) => {
                      const Icon = domain.icon;
                      const isSelected = goalDomain === domain.id;
                      return (
                        <button
                          key={domain.id}
                          type="button"
                          onClick={() => setGoalDomain(domain.id)}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            isSelected
                              ? `border-current bg-gradient-to-br ${domain.gradient} text-white shadow-lg`
                              : isDarkMode
                              ? 'border-slate-600 bg-slate-800/30 text-gray-300 hover:border-slate-500'
                              : 'border-gray-200 bg-white/60 text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <Icon size={24} className="mx-auto mb-2" />
                          <div className="text-sm font-medium">{domain.label}</div>
                          <div className="text-xs opacity-80 mt-1">{domain.description}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Duration Slider */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    {t('goals.field.duration', 'Süre')}: <span className="text-blue-500">{formatDuration(duration, t)}</span>
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="180"
                    step="5"
                    value={duration}
                    onChange={(e) => setDuration(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5dk</span>
                    <span>3s</span>
                  </div>
                </div>

                {/* Difficulty & Repeat */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      {t('goals.field.difficulty', 'Zorluk')}
                    </label>
                    <select
                      value={difficulty}
                      onChange={(e) => setDifficulty(e.target.value)}
                      className={`w-full px-3 py-2 rounded-xl border ${
                        isDarkMode 
                          ? 'bg-slate-800/50 border-slate-700 text-white' 
                          : 'bg-white/80 border-gray-200 text-gray-900'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="easy">{t('goals.difficulty.easy', 'Kolay')}</option>
                      <option value="medium">{t('goals.difficulty.medium', 'Orta')}</option>
                      <option value="hard">{t('goals.difficulty.hard', 'Zor')}</option>
                      <option value="extreme">{t('goals.difficulty.extreme', 'Uzman')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      {t('goals.field.repeat', 'Tekrar')}
                    </label>
                    <select
                      value={repeatPattern}
                      onChange={(e) => setRepeatPattern(e.target.value)}
                      className={`w-full px-3 py-2 rounded-xl border ${
                        isDarkMode 
                          ? 'bg-slate-800/50 border-slate-700 text-white' 
                          : 'bg-white/80 border-gray-200 text-gray-900'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      {Object.entries(REPEAT_PATTERNS).map(([key, pattern]) => (
                        <option key={key} value={key}>
                          {t(pattern.label, pattern.labelFallback)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    {t('goals.field.description', 'Açıklama')} 
                    <span className="text-gray-400">({t('common.optional', 'opsiyonel')})</span>
                  </label>
                  <textarea
                    value={goalDescription}
                    onChange={(e) => setGoalDescription(e.target.value)}
                    placeholder={t('goals.field.description.placeholder', 'Hedefiniz hakkında detaylar...')}
                    rows={3}
                    className={`w-full px-4 py-3 rounded-xl border resize-none ${
                      isDarkMode 
                        ? 'bg-slate-800/50 border-slate-700 text-white placeholder-gray-400' 
                        : 'bg-white/80 border-gray-200 text-gray-900 placeholder-gray-500'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                </div>

                {/* Navigation */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setCurrentStep('quick')}
                    className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                      isDarkMode
                        ? 'bg-slate-700/50 text-gray-300 hover:bg-slate-600/50'
                        : 'bg-gray-100/80 text-gray-700 hover:bg-gray-200/80'
                    }`}
                  >
                    {t('common.back', 'Geri')}
                  </button>
                  <button
                    onClick={() => setCurrentStep('review')}
                    disabled={!isValid}
                    className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                      isValid
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {t('goals.review', 'İncele')}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Review & Insights */}
            {currentStep === 'review' && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                {/* Goal Summary */}
                <div className={`p-4 rounded-2xl ${
                  isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50/80'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <selectedDomain.icon size={24} style={{ color: selectedDomain.color }} />
                    <div>
                      <h3 className="font-semibold text-lg">{goalTitle}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {formatDuration(duration, t)} • {t(`goals.difficulty.${difficulty}`, difficulty)}
                      </p>
                    </div>
                  </div>
                  {goalDescription && (
                    <p className="text-sm text-gray-700 dark:text-gray-300">{goalDescription}</p>
                  )}
                </div>

                {/* Insights & Warnings */}
                <div className="space-y-3">
                  {/* Abort Risk */}
                  <div className={`p-3 rounded-xl flex items-center gap-3 ${
                    abortRisk > 0.7 
                      ? 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800'
                      : abortRisk > 0.4
                      ? 'bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800'
                      : 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800'
                  }`}>
                    {abortRisk > 0.7 ? (
                      <AlertTriangle size={18} className="text-red-500" />
                    ) : abortRisk > 0.4 ? (
                      <AlertCircle size={18} className="text-yellow-500" />
                    ) : (
                      <CheckCircle size={18} className="text-green-500" />
                    )}
                    <div>
                      <div className="text-sm font-medium">
                        {t('goals.insights.abortRisk', 'Bırakma Riski')}: {Math.round(abortRisk * 100)}%
                      </div>
                      <div className="text-xs opacity-75">
                        {abortRisk > 0.7 
                          ? t('goals.insights.high', 'Yüksek - daha kısa süre düşünün')
                          : abortRisk > 0.4
                          ? t('goals.insights.medium', 'Orta - dikkatli olun')
                          : t('goals.insights.low', 'Düşük - sürdürülebilir')
                        }
                      </div>
                    </div>
                  </div>

                  {/* Balance Assessment */}
                  <div className={`p-3 rounded-xl flex items-center gap-3 ${
                    balanceAssessment.score < 0.6
                      ? 'bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
                      : 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800'
                  }`}>
                    <TrendingUp size={18} className="text-blue-500" />
                    <div>
                      <div className="text-sm font-medium">
                        {t('goals.insights.balance', 'Denge Skoru')}: {Math.round(balanceAssessment.score * 100)}%
                      </div>
                      <div className="text-xs opacity-75">
                        {balanceAssessment.recommendation === 'rebalance'
                          ? t('goals.insights.rebalance', 'Diğer alanlarda da hedefler ekleyin')
                          : t('goals.insights.balanced', 'İyi dengede')
                        }
                      </div>
                    </div>
                  </div>

                  {/* XP Estimate */}
                  <div className={`p-3 rounded-xl flex items-center gap-3 ${
                    isDarkMode ? 'bg-purple-900/20 border-purple-800' : 'bg-purple-50 border-purple-200'
                  } border`}>
                    <Sparkles size={18} className="text-purple-500" />
                    <div>
                      <div className="text-sm font-medium">
                        {t('goals.insights.xp', 'Tahmini XP')}: {Math.round(Math.sqrt(duration) * DIFFICULTY_MULTIPLIERS[difficulty] * 10)}
                      </div>
                      <div className="text-xs opacity-75">
                        {t('goals.insights.xpDesc', 'Tamamlandığında kazanılacak')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error Display */}
                {validationError && (
                  <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2">
                    <AlertTriangle size={18} className="text-red-500" />
                    <span className="text-sm text-red-700">{validationError}</span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setCurrentStep('advanced')}
                    className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                      isDarkMode
                        ? 'bg-slate-700/50 text-gray-300 hover:bg-slate-600/50'
                        : 'bg-gray-100/80 text-gray-700 hover:bg-gray-200/80'
                    }`}
                  >
                    {t('common.edit', 'Düzenle')}
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!isValid || isSubmitting}
                    className={`flex-2 py-3 px-6 rounded-xl font-medium transition-colors ${
                      !isValid || isSubmitting
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : `bg-gradient-to-r ${selectedDomain?.gradient} text-white hover:shadow-lg`
                    }`}
                  >
                    {isSubmitting 
                      ? t('common.creating', 'Oluşturuluyor...') 
                      : t('goals.create.submit', 'Hedef Oluştur')
                    }
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreateDisciplineGoalModal;
